/**
 * The noun a group-discount tier bracket counts, pluralised.
 *
 * A tier is matched on the category's selected COUNT, and for a per-unit
 * category one count is one unit — a "Couple" is one step and two people. So
 * "1–4" beside a per-group category means four couples, and rendering it as
 * "1–4 people" states half the number. The builder and the storefront must
 * agree, because the operator sets the bracket in one and the traveller reads
 * it in the other.
 *
 * Lives here rather than in either app because it was written twice and had
 * already drifted twice: the two copies disagreed on whether the `per ` strip
 * was anchored and case-sensitive, and both rendered an operator category
 * "Family" as "Familys".
 */

/** Strip the charge-basis prefix an operator-authored label usually carries:
 *  "per couple" / "Per Couple" → "couple". Anchored and case-insensitive —
 *  an unanchored strip would also eat "per" from the middle of a real noun. */
export function unitNounOf(label: string): string {
  return label.replace(/^per\s+/i, "");
}

/** English plural for an operator-authored unit noun.
 *
 *  Deliberately small: it covers the sibilant rule and consonant + `y`, which
 *  is every case the price-category vocabulary has produced. It is NOT a
 *  general pluraliser and should not grow into one — a category whose plural
 *  this gets wrong wants an explicit operator-supplied label, not another
 *  branch here. */
export function pluralizeUnitNoun(noun: string): string {
  if (/[^aeiou]y$/i.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(noun)) return `${noun}es`;
  return `${noun}s`;
}

/**
 * Plural bracket noun for a tier, from the category's charge basis and label.
 *
 * `perPerson` is the category's own charge basis, not a guess from the label:
 * a per-person category always counts people, whatever its display label says.
 * Callers pass the already-resolved label because the two apps resolve it from
 * different DTO fields — operator-web from `displayLabel ?? chargeText`,
 * traveler-web from a DTO where `displayLabel` has already absorbed `name`.
 */
export function tierUnitNoun(label: string, perPerson: boolean): string {
  if (perPerson) return "people";
  return pluralizeUnitNoun(unitNounOf(label));
}
